/**
 * Enhanced Booking Service Proxy for SightTrails Safaris
 * Redirects all booking operations to the custom Express backend
 */

import { apiService } from './apiService';
import { USE_BACKEND_API } from '../config/apiConfig';
import { supabase } from './supabaseService';

export interface BookingRequest {
  tourId: string;
  customerDetails: any;
  travelers: any[];
  selectedDate: string;
  selectedExtras: any[];
  paymentMethod: string;
  specialRequests?: string;
  dietaryRequirements?: string;
  medicalConditions?: string;
  insuranceRequired: boolean;
  termsAccepted: boolean;
  privacyAccepted: boolean;

  // Pricing context for backend validation
  tier: string;
  nationalityType: string;
  travelDates: { startDate: string; endDate: string };
  pax: { adults: number; children: number };
}

export class BookingService {
  /**
   * Create a new booking via backend or fallback to Supabase
   */
  async createBooking(request: any): Promise<any> {
    const backendData = {
      tourId: request.tourId,
      customerName: request.customerDetails?.name || request.customerName || 'Valued Guest',
      customerEmail: request.customerDetails?.email || request.customerEmail || '',
      customerPhone: request.customerDetails?.phone || request.customerPhone || '',
      customerNationality: request.customerDetails?.nationality || request.customerNationality || 'Kenyan',
      passportNumber: request.customerDetails?.passportNumber || request.passportNumber || '',
      emergencyContactName: request.customerDetails?.emergencyContact?.name || request.emergencyContactName || '',
      emergencyContactPhone: request.customerDetails?.emergencyContact?.phone || request.emergencyContactPhone || '',
      emergencyContactRelationship: request.customerDetails?.emergencyContact?.relationship || request.emergencyContactRelationship || '',
      tourDate: request.selectedDate || request.tourDate,
      specialRequests: request.specialRequests,
      dietaryRequirements: request.dietaryRequirements,
      medicalConditions: request.medicalConditions,
      tier: request.tier,
      nationalityType: request.nationalityType,
      travelDates: request.travelDates,
      pax: request.pax,
      travelers: request.travelers,
      extras: request.selectedExtras || request.extras,
      paymentMethod: request.paymentMethod,
      insuranceRequired: request.insuranceRequired,
      termsAccepted: request.termsAccepted,
      privacyAccepted: request.privacyAccepted,
      status: request.status || 'pending',
      paymentStatus: request.paymentStatus || 'pending',
      // Explicitly include business-critical fields in snake_case too for backend compatibility
      customer_email: request.customerDetails?.email || request.customerEmail || '',
      customer_name: request.customerDetails?.name || request.customerName || 'Valued Guest',
      tour_id: request.tourId,
      total_amount: request.totalAmount || 0,
      currency: request.currency || 'USD'
    };

    if (USE_BACKEND_API) {
      console.log('📡 Redirecting booking to custom backend...');
      try {
        return await apiService.createBooking(backendData);
      } catch (error: any) {
        console.error('❌ Backend booking failed:', error);
        throw error;
      }
    }

    console.log('📡 Saving booking to Supabase...');
    // Map to snake_case for Supabase
    const supabaseData = {
      tour_id: backendData.tourId,
      customer_name: backendData.customerName,
      customer_email: backendData.customerEmail,
      customer_phone: backendData.customerPhone,
      customer_nationality: backendData.customerNationality,
      passport_number: backendData.passportNumber,
      tour_date: backendData.tourDate,
      special_requests: backendData.specialRequests,
      status: 'pending',
      payment_status: 'pending',
      total_amount: request.totalAmount || 0,
      currency: request.currency || 'USD',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase.from('bookings').insert([supabaseData]).select().single();
    if (error) throw error;

    // Also save travelers and extras if tables exist
    if (backendData.travelers && backendData.travelers.length > 0) {
      const travelers = backendData.travelers.map((t: any) => ({ ...t, booking_id: data.id }));
      await supabase.from('travelers').insert(travelers);
    }

    return data;
  }

  /**
   * Update an existing booking
   */
  async updateBooking(id: string, request: any): Promise<any> {
    const updateData: any = { ...request };

    if (USE_BACKEND_API) {
      return await apiService.updateBooking(id, updateData);
    }

    // Supabase update logic
    const supabaseUpdate = {
      status: updateData.status,
      payment_status: updateData.paymentStatus,
      special_requests: updateData.specialRequests,
      updated_at: new Date().toISOString()
    };

    // Map camelCase to snake_case for Supabase where necessary
    if (updateData.customerName) (supabaseUpdate as any).customer_name = updateData.customerName;
    if (updateData.totalAmount) (supabaseUpdate as any).total_amount = updateData.totalAmount;

    const { data, error } = await supabase.from('bookings').update(supabaseUpdate).eq('id', id).select().single();
    if (error) throw error;
    return data;
  }

  async getBookingById(id: string): Promise<any> {
    if (USE_BACKEND_API) {
      return await apiService.getBookingById(id);
    }
    const { data, error } = await supabase.from('bookings').select('*, travelers(*), booking_extras(*)').eq('id', id).single();
    if (error) throw error;
    return data;
  }

  async getBookingsByClient(email: string): Promise<any[]> {
    if (USE_BACKEND_API) {
      // Add getBookingsByEmail to apiService if needed, or filter allBookings
      const all = await apiService.getAllBookings();
      return all.filter((b: any) => b.customer_email === email);
    }
    const { data, error } = await supabase.from('bookings').select('*').eq('customer_email', email);
    if (error) throw error;
    return data || [];
  }

  async getAllBookings(): Promise<any[]> {
    if (USE_BACKEND_API) {
      return await apiService.getAllBookings();
    }
    const { data, error } = await supabase.from('bookings').select('*, travelers(*)');
    if (error) throw error;
    return data || [];
  }

  async updateBookingStatus(id: string, status: string, paymentStatus?: string): Promise<void> {
    if (USE_BACKEND_API) {
      await apiService.updateBookingStatus(id, status, paymentStatus);
      return;
    }
    await supabase.from('bookings').update({ status, payment_status: paymentStatus }).eq('id', id);
  }
}

export const bookingService = new BookingService();
export default bookingService;